/**
 * O3 (SO-38) — human-readable service durations for the owner portal.
 *
 * The API sends durations as `HH:mm:ss` ("01:30:00"); owners must read
 * "1 h 30 min" / «ساعة و30 دقيقة», never the raw wire value. Copy comes from the
 * `mb.duration.*` keys (src/i18n/owner/myBusiness.ts) so both languages stay in
 * the string module; Arabic needs the dual / 3–10 plural forms, picked here.
 */
type TFn = (key: string) => string;

/** `HH:mm:ss` or `HH:mm` → total minutes; null when unparseable. */
export function durationToMinutes(value: string | undefined | null): number | null {
    if (!value) return null;
    const parts = value.split(':');
    if (parts.length < 2) return null;
    const hours = parseInt(parts[0], 10);
    const mins = parseInt(parts[1], 10);
    if (isNaN(hours) || isNaN(mins)) return null;
    return hours * 60 + mins;
}

/** Arabic count form: 1 → one, 2 → two, 3–10 → few, otherwise other. */
function pluralForm(n: number): 'one' | 'two' | 'few' | 'other' {
    if (n === 1) return 'one';
    if (n === 2) return 'two';
    if (n >= 3 && n <= 10) return 'few';
    return 'other';
}

function unit(t: TFn, base: 'mb.duration.minutes' | 'mb.duration.hours', n: number): string {
    return t(`${base}.${pluralForm(n)}`).replace('{n}', String(n));
}

/** Minutes → "45 min" / "1 h" / "1 h 30 min" (en) or «45 دقيقة» / «ساعة» / «ساعة و30 دقيقة» (ar). */
export function formatDurationMinutes(mins: number | null | undefined, t: TFn): string {
    if (mins == null || !Number.isFinite(mins) || mins < 0) return '';
    if (mins < 60) return unit(t, 'mb.duration.minutes', mins);
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    const hours = unit(t, 'mb.duration.hours', h);
    if (m === 0) return hours;
    return t('mb.duration.join')
        .replace('{h}', hours)
        .replace('{m}', unit(t, 'mb.duration.minutes', m));
}

/** `HH:mm:ss` → human duration ('' when unparseable). */
export function formatWireDuration(value: string | undefined | null, t: TFn): string {
    return formatDurationMinutes(durationToMinutes(value), t);
}
